import { useCallback, useEffect, useRef, useState } from 'react';
import { Camera, Check, ChevronLeft, Keyboard, Plus } from 'lucide-react';
import { api } from '../api';
import Ayuda from '../ui/Ayuda';
import Motivo from '../experiencia/Motivo';
import { Cargando } from '../ui/EstadoDePantalla';
import { useSubpantalla } from '../ui/navegacionReact';
import {
  LARGO_CODIGO, codigoCompleto, minutosYSegundos, motivoDeRechazo, normalizarCodigo,
  recortarNombre, useCuentaRegresiva,
} from './codigoDeTerminal';
import { leerErrorDelContrato } from './errorDelContrato';
import { ErrorDelContrato } from './VincularTerminal';
import AgregarTerminal from './AgregarTerminal';
import EscanerDeVinculo from './EscanerDeVinculo';
import { comoDarPermiso, pintarMientrasSeEscribe } from './vinculoEnLinea';

/* =====================================================================
   «Agregar terminal» · vincular en línea, sin tarjeta · contrato 2.11.0
   =====================================================================
   DIRECTRIZ-ACTIVACION-EN-LINEA §2 y DISENO-ACTIVACION-EN-LINEA §6.

     entrada → (escanear | escribir) → vista previa con sucursal, punto y
     para qué sirve → «Vincular» → «Toca Confirmar en la terminal» → Listo.

   La tarjeta de la caja SALE de este flujo (D12): lo de antes (Bluetooth,
   «Darme un número», el asistente de tres pasos) vive en «Otras formas de
   agregar», montado con `sinTarjeta`. El PIN de administrador en la
   terminal se queda como vía alterna (se explica ahí).

   EL FRONTEND NO DECIDE: si no se puede vincular, la vista previa trae el
   motivo y qué hacer del servidor, y el botón se apaga con eso a la vista.
   ===================================================================== */

const BOTON = 'min-h-11 w-full rounded-xl px-3 text-sm font-semibold disabled:opacity-50';
const PRIMARIO = `${BOTON} bg-[var(--color-marca)] text-white`;
const SECUNDARIO = `${BOTON} border border-gray-300 bg-white text-gray-800`;
const GRANDE = 'min-h-14 w-full rounded-xl border-2 px-3 text-base font-semibold inline-flex items-center justify-center gap-2';
const ENLACE = 'min-h-11 inline-flex items-center gap-1 text-sm font-semibold text-gray-700';

const PROPOSITOS = [
  { valor: 'asistencia', texto: 'Pasar lista' },
  { valor: 'inventario', texto: 'Inventario' },
  { valor: 'ambas', texto: 'Ambas' },
];
const TIPOS_PUNTO = [
  { valor: 'ALMACEN', texto: 'Almacén' }, { valor: 'BODEGA', texto: 'Bodega' },
  { valor: 'OFICINA', texto: 'Oficina' }, { valor: 'ANDEN', texto: 'Andén' },
  { valor: 'TIENDA', texto: 'Tienda' },
];
const SEGUNDOS_ENTRE_PREGUNTAS = 2000;

function codigoDePunto(nombre) {
  const base = String(nombre || '').toUpperCase().normalize('NFD').replace(/[^A-Z0-9]/g, '').slice(0, 6) || 'PUNTO';
  return `${base}${Date.now().toString(36).toUpperCase().slice(-4)}`;
}

export default function VincularEnLinea({ contexto, irA, onCerrar }) {
  const precargado = normalizarCodigo(contexto?.vincular || '').codigo;
  const [modo, setModo] = useState(codigoCompleto(precargado) ? 'buscando' : (contexto?.escrito ? 'escribir' : 'entrada'));
  const [otras, setOtras] = useState(false);
  const [otrasContexto, setOtrasContexto] = useState(null);
  const [licencia, setLicencia] = useState(undefined);
  const [codigo, setCodigo] = useState(precargado || normalizarCodigo(contexto?.escrito || '').codigo);
  const [rechazo, setRechazo] = useState(null);
  const [error, setError] = useState(null);
  const [avisoCamara, setAvisoCamara] = useState(null);
  const [datos, setDatos] = useState(null);
  const [eleccion, setEleccion] = useState({ id_sucursal: '', id_ubicacion: '', proposito: 'ambas', nombre: '' });
  const [creandoPunto, setCreandoPunto] = useState(null);   // null | {nombre, tipo, guardando, error}
  const [enviando, setEnviando] = useState(false);
  const [espera, setEspera] = useState(null);                // {confirmar_antes}
  const [final, setFinal] = useState(null);                  // {estado, terminal?, motivo?}
  const conCamara = typeof window !== 'undefined' && window.isSecureContext === true;
  const vence = useCuentaRegresiva(datos?.vence);
  const restante = useCuentaRegresiva(espera?.confirmar_antes);

  useSubpantalla('vincular-otras', otras, () => setOtras(false));
  useSubpantalla('vincular-escaner', modo === 'escanear', () => setModo('entrada'));

  useEffect(() => {
    let vivo = true;
    api.get('/terminales/licencia')
      .then((l) => { if (vivo) setLicencia(l); })
      .catch((e) => { if (vivo) setLicencia(e?.status === 403 ? 'SIN_PERMISO' : null); });
    return () => { vivo = false; };
  }, []);

  const buscar = useCallback(async (cod) => {
    setModo('buscando');
    setError(null);
    try {
      const d = await api.get(`/terminales/vinculo/${cod}`);
      setDatos({ ...d, codigo: cod });
      setEleccion({
        id_sucursal: d.sucursal_sugerida ? String(d.sucursal_sugerida) : '',
        id_ubicacion: d.punto_sugerido ? String(d.punto_sugerido) : '',
        proposito: d.proposito_sugerido || 'ambas',
        nombre: recortarNombre(d.nombre_sugerido || ''),
      });
      setModo('vista');
    } catch (e) {
      // UNA TERMINAL CON FIRMWARE ANTERIOR A 2.11 enseña el código del flujo
      // de antes (`vincular/iniciar`): si existe allí, se abre ese asistente
      // con el código puesto, sin la tarjeta.
      if (e?.status === 404) {
        try {
          await api.get(`/terminal/v2/vincular/${cod}`);
          setOtrasContexto({ vincular: cod });
          setOtras(true);
          return;
        } catch { /* tampoco es de antes: se dice lo del código nuevo */ }
      }
      setError(leerErrorDelContrato(e, { porEstado: { 404: 'CODIGO_DESCONOCIDO', 410: 'CODIGO_VENCIDO' } }));
      setModo('escribir');
    }
  }, []);

  const buscado = useRef(null);
  useEffect(() => {
    if (!codigoCompleto(precargado) || buscado.current === precargado) return;
    buscado.current = precargado;
    buscar(precargado);
  }, [precargado, buscar]);

  // La espera del toque: una pregunta cada 2 s mientras la pantalla está abierta.
  useEffect(() => {
    if (!espera || !datos) return undefined;
    let vivo = true;
    let fallos = 0;
    const preguntar = async () => {
      try {
        const e = await api.get(`/terminales/vinculo/${datos.codigo}/estado`);
        fallos = 0;
        if (!vivo || e.estado === 'POR_CONFIRMAR') return;
        setFinal(e);
        setEspera(null);
      } catch {
        fallos += 1;
        if (fallos >= 2 && vivo) { setFinal({ estado: 'SIN_RESPUESTA' }); setEspera(null); }
      }
    };
    const t = setInterval(preguntar, SEGUNDOS_ENTRE_PREGUNTAS);
    if (t && typeof t.unref === 'function') t.unref();
    return () => { vivo = false; clearInterval(t); };
  }, [espera, datos]);

  function teclear(e) {
    const { codigo: limpio, rechazados } = normalizarCodigo(e.target.value);
    setCodigo(limpio);
    setRechazo(motivoDeRechazo(rechazados));
    setError(null);
    // Busca sola al completar los seis (DIRECTRIZ §2).
    if (codigoCompleto(limpio) && limpio !== codigo) buscar(limpio);
  }

  const escaneado = useCallback((cod) => { setCodigo(cod); buscar(cod); }, [buscar]);
  const permisoNegado = useCallback(() => {
    setAvisoCamara(`La cámara está bloqueada para esta página. ${comoDarPermiso()} Mientras tanto, escribe el código.`);
    setModo('escribir');
  }, []);

  function otraVez() {
    setDatos(null); setCodigo(''); setRechazo(null); setError(null); setEspera(null);
    setFinal(null); setAvisoCamara(null); setCreandoPunto(null); setModo('entrada');
  }

  async function vincularAhora(e) {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      const r = await api.post(`/terminales/vinculo/${datos.codigo}`, {
        id_sucursal: eleccion.id_sucursal ? Number(eleccion.id_sucursal) : null,
        id_ubicacion: eleccion.id_ubicacion ? Number(eleccion.id_ubicacion) : null,
        proposito: eleccion.proposito,
        nombre: recortarNombre(eleccion.nombre).trim() || null,
      });
      setEspera({ confirmar_antes: r.confirmar_antes });
      setModo('esperando');
    } catch (err) {
      setError(leerErrorDelContrato(err, { porEstado: { 404: 'CODIGO_DESCONOCIDO', 410: 'CODIGO_VENCIDO' } }));
    } finally {
      setEnviando(false);
    }
  }

  async function guardarPunto() {
    const p = creandoPunto;
    if (!p?.nombre?.trim()) return;
    setCreandoPunto({ ...p, guardando: true, error: null });
    try {
      const u = await api.post('/ubicaciones', {
        codigo: codigoDePunto(p.nombre), nombre: p.nombre.trim(), tipo: p.tipo,
        id_sucursal: eleccion.id_sucursal ? Number(eleccion.id_sucursal) : datos.sucursal_sugerida,
      });
      const nuevo = { id: u.id, nombre: u.nombre || p.nombre.trim(), tipo: p.tipo,
                      id_sucursal: u.id_sucursal ?? (Number(eleccion.id_sucursal) || datos.sucursal_sugerida) };
      setDatos((d) => ({ ...d, puntos: [...(d.puntos || []), nuevo] }));
      setEleccion((el) => ({ ...el, id_ubicacion: String(nuevo.id) }));
      setCreandoPunto(null);
    } catch (err) {
      setCreandoPunto({ ...p, guardando: false, error: leerErrorDelContrato(err) });
    }
  }

  function verTerminales() {
    if (onCerrar) onCerrar();
    else irA?.('terminales');
  }

  // --- Otras formas: el asistente de antes, SIN la tarjeta --------------
  if (otras) {
    return (
      <div className="max-w-xl space-y-3" data-vincular="otras">
        <button type="button" onClick={() => { setOtras(false); setOtrasContexto(null); setModo('entrada'); }} className={ENLACE}>
          <ChevronLeft className="h-4 w-4" aria-hidden="true" /> Volver a vincular en línea
        </button>
        <p className="rounded-xl border border-gray-200 bg-white p-3 text-sm text-gray-700" data-pin-admin="">
          También puedes activarla desde la propia terminal con el PIN de una persona que administra terminales: en su pantalla elige «Con PIN».
        </p>
        <AgregarTerminal contexto={otrasContexto} irA={irA} onCerrar={onCerrar} sinTarjeta />
      </div>
    );
  }

  const sinPermiso = licencia === 'SIN_PERMISO';
  const sinDerecho = licencia && typeof licencia === 'object' && licencia.puede_conectar === false && !licencia.compradas;
  const motivoSinDerecho = sinDerecho ? `${licencia.motivo || ''} ${licencia.que_hacer || ''}`.trim() : null;
  const puntos = (datos?.puntos || []).filter((p) => !eleccion.id_sucursal
    || String(p.id_sucursal) === String(eleccion.id_sucursal));
  const faltaPunto = datos && !eleccion.id_ubicacion
    ? (puntos.length ? 'Elige el punto donde va a trabajar la terminal.'
      : 'Esta sucursal no tiene ningún lugar donde poner una terminal. Crea uno con «Crear punto».')
    : null;
  const vencido = vence === 0;
  const noPuede = datos && datos.puede_vincular === false;
  const motivoVincular = !datos ? null
    : noPuede ? `${datos.motivo || ''} ${datos.que_hacer || ''}`.trim()
    : vencido ? 'El código venció: la terminal ya enseña otro. Escríbelo o escanéalo otra vez.'
    : faltaPunto;
  const faltan = LARGO_CODIGO - codigo.length;
  const hace = datos?.en_linea_hace_s ?? null;
  const colorHace = hace === null ? 'text-gray-700' : hace <= 15 ? 'text-green-800' : hace <= 60 ? 'text-amber-800' : 'text-red-700';

  return (
    <div className="max-w-xl space-y-4 pb-24" data-vincular="en-linea">
      <header className="space-y-1">
        {onCerrar && (
          <button type="button" onClick={onCerrar} className={ENLACE}>
            <ChevronLeft className="h-4 w-4" aria-hidden="true" /> Volver a mis terminales
          </button>
        )}
        <h1 className="text-xl font-bold text-gray-900">Agregar terminal</h1>
        <p className="text-sm text-gray-600">Vincula una terminal a tu empresa con el código de su pantalla.</p>
      </header>
      <Ayuda id="terminal.enlinea.pantalla" />

      {licencia === undefined && <Cargando filas={1} etiqueta="Revisando tus terminales…" />}

      {sinPermiso && (
        <section className="space-y-2 rounded-xl border border-amber-200 bg-amber-50 p-4" data-vincular="sin-permiso">
          <h2 className="text-base font-semibold text-amber-900">Tu usuario no tiene permiso para agregar terminales</h2>
          <p className="text-sm text-amber-900">Pide a quien administra TEGO en tu empresa el permiso «Administrar terminales», o que agregue la terminal.</p>
        </section>
      )}

      {!sinPermiso && modo === 'entrada' && (
        <section className="space-y-3" data-vincular="entrada">
          <Motivo id="motivo-sin-derecho-vincular" cuando={!!sinDerecho}>{motivoSinDerecho}</Motivo>
          {sinDerecho && (
            <button type="button" onClick={() => irA?.('suscripcion')} className={SECUNDARIO}>Ver planes con terminales</button>
          )}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <button type="button" onClick={() => { setAvisoCamara(null); setModo('escanear'); }}
                      disabled={!conCamara || !!sinDerecho}
                      aria-describedby={!conCamara ? 'motivo-sin-camara' : sinDerecho ? 'motivo-sin-derecho-vincular' : undefined}
                      className={`${GRANDE} border-[var(--color-marca)] bg-white text-gray-900 disabled:opacity-50`}
                      data-accion="escanear">
                <Camera className="h-5 w-5" aria-hidden="true" /> Escanear el QR de la terminal
              </button>
              <Motivo id="motivo-sin-camara" cuando={!conCamara}>La cámara necesita una conexión segura. Escribe el código.</Motivo>
            </div>
            <button type="button" onClick={() => setModo('escribir')} disabled={!!sinDerecho}
                    aria-describedby={sinDerecho ? 'motivo-sin-derecho-vincular' : undefined}
                    className={`${GRANDE} border-[var(--color-marca)] bg-white text-gray-900 disabled:opacity-50`}
                    data-accion="escribir">
              <Keyboard className="h-5 w-5" aria-hidden="true" /> Escribir el código
            </button>
          </div>
          <button type="button" onClick={() => setOtras(true)} className={ENLACE} data-accion="otras-formas">
            Otras formas de agregar
          </button>
        </section>
      )}

      {!sinPermiso && modo === 'escanear' && (
        <EscanerDeVinculo onCodigo={escaneado} onPermisoNegado={permisoNegado} onCerrar={() => setModo('entrada')} />
      )}

      {!sinPermiso && (modo === 'escribir' || modo === 'buscando') && (
        <form onSubmit={(e) => { e.preventDefault(); if (codigoCompleto(codigo)) buscar(codigo); }}
              className="space-y-3 rounded-xl border border-gray-200 bg-white p-4" data-vincular="escribir">
          {avisoCamara && (
            <p role="status" className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
               data-aviso-camara="">{avisoCamara}</p>
          )}
          <label htmlFor="vincular-codigo" className="block text-sm font-medium text-gray-700">El código que enseña la pantalla de la terminal</label>
          <input id="vincular-codigo" className="entrada text-center font-mono text-2xl tracking-widest"
                 value={pintarMientrasSeEscribe(codigo)} onChange={teclear}
                 inputMode="text" autoCapitalize="characters" autoComplete="off" autoCorrect="off"
                 spellCheck={false} enterKeyHint="search" maxLength={12} placeholder="EET-5D6"
                 disabled={modo === 'buscando'} data-codigo-vinculo="" />
          <Ayuda id="terminal.enlinea.codigo" />
          {rechazo && <p role="alert" className="text-sm text-red-700">{rechazo}</p>}
          <ErrorDelContrato error={error} />
          {modo === 'buscando' && <Cargando filas={1} etiqueta="Buscando la terminal…" />}
          <Motivo id="motivo-vincular-codigo" cuando={!codigoCompleto(codigo) && modo !== 'buscando'}>
            {`Faltan ${faltan} ${faltan === 1 ? 'carácter' : 'caracteres'}: el código tiene ${LARGO_CODIGO}.`}
          </Motivo>
          <button type="submit" disabled={!codigoCompleto(codigo) || modo === 'buscando'}
                  aria-describedby={!codigoCompleto(codigo) ? 'motivo-vincular-codigo' : undefined} className={PRIMARIO}>
            {modo === 'buscando' ? 'Buscando…' : 'Buscar'}
          </button>
          <button type="button" onClick={otraVez} className={SECUNDARIO}>Volver</button>
        </form>
      )}

      {!sinPermiso && modo === 'vista' && datos && (
        <form onSubmit={vincularAhora} className="space-y-4" data-vincular="vista">
          <section className={`rounded-xl border-2 p-4 text-sm ${noPuede ? 'border-red-300 bg-red-50' : 'border-green-300 bg-green-50'}`}
                   data-vista-previa="">
            <p className="break-words font-semibold text-gray-900">
              {`Terminal ${datos.codigo_pantalla} · serie ${datos.serie} · `}
              <span className={colorHace}>{`en línea hace ${hace} s`}</span>
            </p>
            <p className="mt-1 break-words text-gray-800">{`Se vinculará a ${datos.empresa}`}</p>
            {hace !== null && hace > 15 && hace <= 60 && <p className="mt-1 text-amber-800">Revisa que la terminal siga encendida.</p>}
            {vence !== null && (
              <p role="status" className={vencido ? 'mt-1 font-semibold text-red-700' : 'mt-1 text-gray-700'}>
                {vencido ? 'Este código ya venció: la terminal enseña otro.' : `Vence en ${minutosYSegundos(vence)}`}
              </p>
            )}
            {noPuede && (
              <div role="alert" className="mt-2 text-red-900" data-no-puede="">
                <strong className="block">{datos.motivo}</strong>
                <span>{datos.que_hacer}</span>
              </div>
            )}
            {noPuede && datos.oferta && (
              <button type="button" onClick={() => irA?.('suscripcion')} className={`${SECUNDARIO} mt-2`}>{datos.oferta.texto}</button>
            )}
          </section>

          {!noPuede && (
            <section className="space-y-3 rounded-xl border border-gray-200 bg-white p-4">
              {(datos.sucursales || []).length > 1 && (
                <div>
                  <label htmlFor="vincular-sucursal" className="block text-sm font-medium text-gray-700">Sucursal</label>
                  <select id="vincular-sucursal" className="entrada mt-1" value={eleccion.id_sucursal}
                          onChange={(e) => setEleccion((el) => ({ ...el, id_sucursal: e.target.value, id_ubicacion: '' }))}>
                    {datos.sucursales.map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}
                  </select>
                  <Ayuda id="terminal.enlinea.sucursal" />
                </div>
              )}
              <div>
                <label htmlFor="vincular-punto" className="block text-sm font-medium text-gray-700">Punto</label>
                {puntos.length > 0 && (
                  <select id="vincular-punto" className="entrada mt-1" value={eleccion.id_ubicacion}
                          onChange={(e) => setEleccion((el) => ({ ...el, id_ubicacion: e.target.value }))}>
                    <option value="">Elige el punto</option>
                    {puntos.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                  </select>
                )}
                <Ayuda id="terminal.enlinea.punto" />
                {!creandoPunto && (
                  <button type="button" className={`${ENLACE} mt-1`} data-accion="crear-punto"
                          onClick={() => setCreandoPunto({ nombre: '', tipo: 'ALMACEN', guardando: false, error: null })}>
                    <Plus className="h-4 w-4" aria-hidden="true" /> Crear punto
                  </button>
                )}
                {creandoPunto && (
                  <div className="mt-2 space-y-2 rounded-xl border border-gray-200 p-3" data-crear-punto="">
                    <label htmlFor="punto-nombre" className="block text-sm font-medium text-gray-700">Nombre del punto</label>
                    <input id="punto-nombre" className="entrada" value={creandoPunto.nombre} maxLength={60}
                           placeholder="Entrada" onChange={(e) => setCreandoPunto({ ...creandoPunto, nombre: e.target.value })} />
                    <label htmlFor="punto-tipo" className="block text-sm font-medium text-gray-700">Qué es</label>
                    <select id="punto-tipo" className="entrada" value={creandoPunto.tipo}
                            onChange={(e) => setCreandoPunto({ ...creandoPunto, tipo: e.target.value })}>
                      {TIPOS_PUNTO.map((t) => <option key={t.valor} value={t.valor}>{t.texto}</option>)}
                    </select>
                    <ErrorDelContrato error={creandoPunto.error} />
                    <Motivo id="motivo-crear-punto" cuando={!creandoPunto.nombre.trim()}>Escribe el nombre del punto.</Motivo>
                    <button type="button" onClick={guardarPunto} className={PRIMARIO}
                            disabled={!creandoPunto.nombre.trim() || creandoPunto.guardando}
                            aria-describedby={!creandoPunto.nombre.trim() ? 'motivo-crear-punto' : undefined}>
                      {creandoPunto.guardando ? 'Creando…' : 'Crear este punto'}
                    </button>
                    <button type="button" onClick={() => setCreandoPunto(null)} className={SECUNDARIO}>Cancelar</button>
                  </div>
                )}
              </div>
              <fieldset>
                <legend className="text-sm font-medium text-gray-700">Uso de la terminal</legend>
                <div className="mt-1 grid grid-cols-1 gap-2 sm:grid-cols-3">
                  {PROPOSITOS.map((p) => (
                    <label key={p.valor} className={`flex min-h-11 items-center gap-2 rounded-xl border px-3 text-sm ${eleccion.proposito === p.valor ? 'border-[var(--color-marca)] bg-gray-50 font-semibold' : 'border-gray-300'}`}>
                      <input type="radio" name="vincular-proposito" value={p.valor} checked={eleccion.proposito === p.valor}
                             onChange={() => setEleccion((el) => ({ ...el, proposito: p.valor }))} />
                      {p.texto}
                    </label>
                  ))}
                </div>
                <Ayuda id="terminal.enlinea.proposito" />
              </fieldset>
              <details>
                <summary className="min-h-11 cursor-pointer py-2 text-sm font-semibold text-gray-700">Más opciones</summary>
                <label htmlFor="vincular-nombre" className="block text-sm font-medium text-gray-700">Nombre de la terminal</label>
                <input id="vincular-nombre" className="entrada mt-1" value={eleccion.nombre} maxLength={21}
                       onChange={(e) => setEleccion((el) => ({ ...el, nombre: recortarNombre(e.target.value) }))} />
              </details>
            </section>
          )}

          <ErrorDelContrato error={error} />
          {enviando && <Cargando filas={1} etiqueta="Vinculando…" />}
          <Motivo id="motivo-vincular" cuando={!!motivoVincular && !enviando}>{motivoVincular}</Motivo>
          <button type="submit" disabled={!!motivoVincular || enviando}
                  aria-describedby={motivoVincular ? 'motivo-vincular' : undefined}
                  className={PRIMARIO} data-accion="vincular">
            {enviando ? 'Vinculando…' : 'Vincular'}
          </button>
          <button type="button" onClick={otraVez} className={SECUNDARIO}>Usar otro código</button>
        </form>
      )}

      {modo === 'esperando' && !final && (
        <section className="space-y-3 rounded-xl border-2 border-[var(--color-marca)] bg-white p-4 text-center" data-vincular="esperando">
          <div className="mx-auto h-16 w-24 animate-pulse rounded-xl bg-gray-800" aria-hidden="true" />
          <p role="status" className="text-lg font-semibold text-gray-900">Toca Confirmar en la terminal</p>
          {restante !== null && <p className="text-sm text-gray-700">{`Tienes ${minutosYSegundos(restante)}`}</p>}
          <Ayuda id="terminal.enlinea.confirmar" />
        </section>
      )}

      {final && (
        <ResultadoFinal final={final} onOtra={otraVez} onVer={verTerminales} />
      )}
      <Ayuda id="terminal.enlinea.ficha" />
    </div>
  );
}

function ResultadoFinal({ final, onOtra, onVer }) {
  if (final.estado === 'ACTIVADA') {
    const t = final.terminal || {};
    const donde = [t.nombre, t.sucursal ? `Sucursal ${t.sucursal}` : null].filter(Boolean).join(' · ');
    return (
      <section className="space-y-3 rounded-xl border-2 border-green-300 bg-green-50 p-4" data-vincular="listo">
        <p role="status" className="flex items-start gap-2 text-base font-semibold text-green-900">
          <Check className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" /> {`Listo: ${donde}`}
        </p>
        <button type="button" onClick={onOtra} className={PRIMARIO} data-accion="agregar-otra">Agregar otra</button>
        <button type="button" onClick={onVer} className={SECUNDARIO}>Ver mis terminales</button>
      </section>
    );
  }
  const textos = {
    RECHAZADO: ['En la terminal se tocó Cancelar.', 'Si fue un error, escanea el código nuevo.'],
    SIN_RESPUESTA: ['No se pudo saber si la terminal confirmó.', 'Revisa su pantalla: si dice Lista, ya quedó.'],
  };
  const [que, hacer] = textos[final.estado]
    || ['La terminal no confirmó a tiempo.', 'Ve con la terminal, escanea el código nuevo y toca Confirmar.'];
  return (
    <section className="space-y-3 rounded-xl border-2 border-amber-300 bg-amber-50 p-4" data-vincular="sin-toque">
      <p role="status" className="text-base font-semibold text-amber-900">{que}</p>
      <p className="text-sm text-amber-900">{hacer}</p>
      <button type="button" onClick={onOtra} className={PRIMARIO}>Volver a escanear</button>
    </section>
  );
}
